import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../App";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [isOffline, setIsOffline] = useState(false);
  const offlineTimeoutRef = React.useRef(null);
  const isOfflineRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Token beim Laden prüfen
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('userToken');
        
        // Routen, die ohne Login zugänglich sind
        const isResetPasswordRoute = location.pathname.startsWith('/reset-password');
        const isSetPasswordRoute = location.pathname.startsWith('/set-password');
        const isLoginRoute = location.pathname === '/login';
  
        if (!token) {
          setUser(null);
          setLoading(false);
          // Nur umleiten, wenn wir nicht bereits auf einer "public" Route sind
          if (!isResetPasswordRoute && !isSetPasswordRoute && !isLoginRoute) {
            navigate('/login');
          }
          return;
        }
  
        // Wenn Token vorhanden ist, überprüfe den Status gegen das Backend
        const { data } = await axios.get(`${API_BASE_URL}/api/check`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 5000 // Timeout hinzufügen, falls DB/Server nicht reagiert
        });
  
        if (!data.status && !isResetPasswordRoute && !isSetPasswordRoute) {
          setUser(null);
          navigate('/login');
        } else {
          setUser(data);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setUser(null);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
  
    if (loading) {
      checkAuth();
    }
  }, [loading, navigate, location.pathname]);
  
  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/login`, { email, password });
      localStorage.setItem('userToken', data.token);
      
      // Berechne und speichere die Ablaufzeit
      const expiresIn = 60 * 60 * 1000; // 1 Stunde
      const expiryDate = new Date().getTime() + expiresIn;
      localStorage.setItem('expiryDate', expiryDate);

      setUser(data);
      navigate("/");
    } catch (error) {
      console.error("Login-Fehler:", error);
      if (error.response) {
        toast.error(error.response.data.message || "Benutzername und/oder Passwort falsch");
      } else {
        toast.error("Server nicht erreichbar.");
      }
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/logout`);
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      localStorage.removeItem('userToken');
      localStorage.removeItem('expiryDate');
      navigate("/login");
    }
  };

  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('expiryDate');
    setUser(null);
    setIsOffline(false); // Reset offline status to remove overlay
    isOfflineRef.current = false;
    if (offlineTimeoutRef.current) {
      clearTimeout(offlineTimeoutRef.current);
      offlineTimeoutRef.current = null;
    }
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // Bei Erfolg Offline-Status zurücksetzen
        if (isOfflineRef.current) {
          setIsOffline(false);
          isOfflineRef.current = false;
          if (offlineTimeoutRef.current) {
            clearTimeout(offlineTimeoutRef.current);
            offlineTimeoutRef.current = null;
          }
          toast.success("Verbindung wiederhergestellt.", { toastId: "online-toast" });
        }
        return response;
      },
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const isLoginRequest = url?.includes("/api/login");

        // Netzwerkfehler oder Server nicht erreichbar (nicht für Login-Anfragen)
        if ((!error.response || status === 502 || status === 503 || status === 504) && !isLoginRequest) {
          if (!isOfflineRef.current) {
            isOfflineRef.current = true;
            setIsOffline(true);
            toast.warning("Verbindung zum Server verloren. Versuche Wiederherstellung...", {
                toastId: "offline-toast"
            });
            
            // 5 Sekunden Grace Period
            offlineTimeoutRef.current = setTimeout(() => {
              toast.error("Server nicht erreichbar. Automatisches Logout.", {
                toastId: "logout-toast"
              });
              handleSessionExpired();
            }, 5000);
          }
        } else if (status === 401 && !isLoginRequest) {
          toast.info("Sitzung abgelaufen. Bitte erneut anmelden.");
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, [handleSessionExpired]);

  const checkTokenExpiry = useCallback(() => {
    const expiryDate = localStorage.getItem('expiryDate');
    const currentTime = new Date().getTime();
  
    if (expiryDate && currentTime > expiryDate) {
      toast.info("Sitzung abgelaufen.");
      handleSessionExpired();
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    const intervalId = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(intervalId);
  }, [checkTokenExpiry]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isOffline }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
